/**
 * useVerifyPhotoCoverage — calls the verify-photo-coverage Edge
 * Function (Photo Coverage Part 3). Returns the AI-decided coverage
 * verdict and the list of categories still missing.
 *
 * The cleaner UI invokes this BEFORE pressing the submit endpoint.
 * The Edge Function writes the verdict to
 * cleaning_tasks.photo_coverage_status; submit's CHECK 3 reads the
 * cached value and refuses when status is 'incomplete' or 'pending'.
 *
 * 'unavailable' (Anthropic upstream outage) does NOT block submit —
 * the documented manual override is supervisor approval per
 * CLAUDE.md ERROR 16. The hook surfaces the status so the UI can
 * disclose to the cleaner that AI was unavailable.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase } from './useSupabase'
import { AppError }    from '@/lib/errors/AppError'

export type CoverageStatus = 'covered' | 'incomplete' | 'unavailable'
export type QualityStatus  = 'pending' | 'passed' | 'defects_found' | 'unavailable'

interface VerifyVars {
  taskId: string
}

/** One cleanliness defect the AI flagged (migration 20260622000002). */
export interface DefectFinding {
  code: string
  note: string
}

export interface VerifyResult {
  task_id:            string
  status:             CoverageStatus
  /** Cleanliness verdict from the same vision pass. 'defects_found'
   *  hard-blocks submit (the submit RPC also refuses with
   *  PHOTO_DEFECTS_FOUND); the UI surfaces the defect list. */
  quality_status:     QualityStatus
  defects:            DefectFinding[]
  missing_categories: string[]
  ai_notes:           string
  /** Photos on the task beyond VERIFY_COVERAGE_PHOTO_CAP that ReadyScan did
   *  NOT examine. 0 on the normal path. Non-zero means this verdict covers a
   *  subset, which the UI discloses rather than presenting as a full scan. */
  photos_truncated:   number
}

export function useVerifyPhotoCoverage(): UseMutationResult<VerifyResult, AppError, VerifyVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<VerifyResult, AppError, VerifyVars>({
    mutationFn: async ({ taskId }): Promise<VerifyResult> => {
      const { data, error } = await supabase.functions.invoke<VerifyResult>('verify-photo-coverage', {
        body:    { cleaning_task_id: taskId },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        // `context` is the Response object on a non-2xx (FunctionsHttpError);
        // its body is a ReadableStream and must be read via .clone().json(),
        // not treated as a string — otherwise the real verdict code is lost
        // and the cleaner sees a generic "non-2xx" message. See
        // useSignInWithPin for the canonical pattern.
        const ctx  = error as unknown as { context?: Response }
        let code   = 'COVERAGE_VERIFY_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
          cause:       error,
        })
      }
      if (!data) {
        throw new AppError({
          code:        'EMPTY_RESPONSE',
          message:     'No data',
          userMessage: 'Coverage verification returned no result.',
          status:      500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      // The Edge Function wrote photo_coverage_status — refresh the
      // task query so a subsequent submit reads the new cached value.
      void queryClient.invalidateQueries({ queryKey: ['task', vars.taskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_TASK_ID':         return 'Task id is missing or malformed.'
    case 'TASK_NOT_FOUND':          return 'This task could not be found.'
    case 'FORBIDDEN_OWNERSHIP':     return 'You can only verify photos for your own task.'
    case 'FORBIDDEN_ORG':           return 'This task belongs to a different organization.'
    case 'FORBIDDEN_ROLE':          return 'Your role is not allowed to verify photo coverage.'
    case 'RATE_LIMITED':            return 'Too many verifications in a row. Try again in a moment.'
    case 'PERSIST_FAILED':          return 'Could not save the AI coverage verdict. Try again.'
    case 'INVALID_IDEMPOTENCY_KEY':
    case 'IDEMPOTENCY_KEY_REUSED':  return 'Idempotency error — refresh and try again.'
    case 'INVALID_BODY':            return 'Coverage verification received an invalid request.'
    case 'METHOD_NOT_ALLOWED':      return 'Coverage verification request used the wrong method.'
    case 'COVERAGE_VERIFY_FAILED':
    default:                        return detail || 'Could not verify photo coverage.'
  }
}
